import React, { useEffect, useRef, useState } from "react";
import StatGridBlock from "./blocks/StatGridBlock";
import PipelineBlock from "./blocks/PipelineBlock";
import DetailBlock from "./blocks/DetailBlock";
import TimelineBlock from "./blocks/TimelineBlock";
import ListBlock from "./blocks/ListBlock";
import ActionsPanelBlock from "./blocks/ActionsPanelBlock";
import EmptyStateBlock from "./blocks/EmptyStateBlock";
import MarkdownBlock from "./blocks/MarkdownBlock";
import GenericBlockViewer from "./GenericBlockViewer";
import { emitUxTelemetry } from "../../telemetry/uxTelemetry";

/**
 * Block type -> component registry.
 *
 * Only the 8 kinds with active production emitters are registered. The
 * surrounding 17 V2 vocabulary kinds (table / checklist / source_list /
 * code_diff / json_viewer / file_preview / html / rich_timeline /
 * pill_select / multi_pill / toggle_row / default_field /
 * confirm_action / conditional_section / kanban / dashboard / calendar)
 * were removed in the 2026-05-11 total-closeout canvas prune — they had
 * zero production emitters and accumulated as speculative substrate.
 *
 * `markdown` is kept: emitted by the workspace_app HTML-wrap fallback at
 * `hank/src/api/routes/chat-workspace-app-artifact.ts:97` when the agent
 * builds an app without a structured renderSpec.
 *
 * Unknown types fall through to GenericBlockViewer (safety net for any
 * future renderer drift or new agent-emitted block kinds).
 */
const BLOCK_RENDERERS: Record<string, React.ComponentType<{ payload: Record<string, unknown> }>> = {
  // Workspace render-spec compiler target kinds — payload shapes mirror
  // packages/design-system/src/workspace/render-spec-compiler.ts so a block
  // looks identical whether it's compiled to sandbox HTML or rendered in
  // the React canvas.
  stat_grid: StatGridBlock,
  pipeline: PipelineBlock,
  detail: DetailBlock,
  timeline: TimelineBlock,
  list: ListBlock,
  actions_panel: ActionsPanelBlock,
  empty_state: EmptyStateBlock,
  // workspace_app HTML-wrap fallback emitter — kept for back-compat with
  // agent-built apps that ship HTML but no structured spec.
  markdown: MarkdownBlock,
};

/**
 * Metadata for known block types (used in headers).
 */
const BLOCK_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  stat_grid: { label: "Stat Grid", icon: "stat" },
  pipeline: { label: "Pipeline", icon: "pipe" },
  detail: { label: "Detail", icon: "dtl" },
  timeline: { label: "Timeline", icon: "time" },
  list: { label: "List", icon: "lst" },
  actions_panel: { label: "Actions", icon: "act" },
  empty_state: { label: "Empty State", icon: "mty" },
  markdown: { label: "Markdown", icon: "md" },
};

interface BlockData {
  blockId: string;
  type: string;
  version: number;
  payload: Record<string, unknown>;
  renderHints?: Record<string, string>;
  provenance?: {
    toolName?: string;
    runId?: string;
    sourceUrl?: string;
  };
  actions?: Array<{
    actionId: string;
    label: string;
    type: string;
    confirmRequired: boolean;
  }>;
}

interface BlockRendererProps {
  block: BlockData;
  interactive?: boolean;
  onBlockUpdate?: (blockId: string, payload: Record<string, unknown>) => void;
  onBlockApprove?: (blockId: string, notes?: string) => void;
  onBlockReject?: (blockId: string, notes?: string) => void;
  /**
   * Reserved for future conditional-rendering use. Historically wired to the
   * removed `conditional_section` block kind. Kept on the props interface
   * because callers (Canvas page) still pass it; unused for now.
   */
  answers?: Record<string, unknown>;
}

/**
 * Renders a single canvas block by dispatching to the appropriate renderer.
 * Falls back to GenericBlockViewer for unknown types.
 */
export default function BlockRenderer({
  block,
  interactive = false,
  onBlockUpdate: _onBlockUpdate,
  onBlockApprove,
  onBlockReject,
  answers: _answers,
}: BlockRendererProps) {
  const Renderer = BLOCK_RENDERERS[block.type];
  const meta = BLOCK_TYPE_LABELS[block.type];

  const [reviewNotes, setReviewNotes] = useState("");
  const [showReview, setShowReview] = useState(false);
  const telemetrySent = useRef(false);

  useEffect(() => {
    if (telemetrySent.current) return;
    telemetrySent.current = true;
    emitUxTelemetry("ux.agent_element.rendered", {
      elementType: "canvas_block",
      elementId: block.blockId,
    });
    if (!Renderer) {
      emitUxTelemetry("ux.agent_element.fallback_rendered", {
        elementType: "canvas_block",
        elementId: block.blockId,
      });
    }
  }, [Renderer, block.blockId]);

  return (
    <div
      data-block-id={block.blockId}
      data-block-type={block.type}
      className="rounded-lg overflow-hidden"
      style={{
        border: "1px solid var(--c-border)",
        backgroundColor: "var(--c-surface)",
      }}
    >
      {/* Block header */}
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{
          backgroundColor: "var(--c-surface-2)",
          borderBottom: "1px solid var(--c-border)",
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: meta ? "var(--c-primary-subtle)" : "var(--c-surface-2)",
              color: meta ? "var(--c-primary)" : "var(--c-warning)",
            }}
          >
            {meta?.icon ?? block.type.slice(0, 3)}
          </span>
          <span className="text-xs font-medium" style={{ color: "var(--c-text-secondary)" }}>
            {meta?.label ?? block.type}
          </span>
          {block.version > 1 && (
            <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
              v{block.version}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Provenance */}
          {block.provenance?.toolName && (
            <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
              via {block.provenance.toolName}
            </span>
          )}

          {/* Phase 2: Review toggle */}
          {interactive && (onBlockApprove || onBlockReject) && (
            <button
              onClick={() => setShowReview(!showReview)}
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: showReview ? "var(--c-primary-subtle)" : "var(--c-surface-3)",
                color: showReview ? "var(--c-primary)" : "var(--c-text-faint)",
                border: "1px solid var(--c-border)",
              }}
            >
              {showReview ? "Hide Review" : "Review"}
            </button>
          )}
        </div>
      </div>

      {/* Block content */}
      <div className="p-3">
        {Renderer ? (
          <Renderer payload={block.payload} />
        ) : (
          <GenericBlockViewer type={block.type} payload={block.payload} />
        )}
      </div>

      {/* Phase 2: Review panel (approve/reject with notes) */}
      {interactive && showReview && (
        <div
          className="px-3 py-3 space-y-2"
          style={{ borderTop: "1px solid var(--c-border)", backgroundColor: "var(--c-surface-2)" }}
        >
          <textarea
            placeholder="Review notes (optional)..."
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            className="w-full rounded-lg px-3 py-2 text-xs"
            style={{
              backgroundColor: "var(--c-input-bg)",
              border: "1px solid var(--c-border)",
              color: "var(--c-text)",
              minHeight: 48,
              resize: "vertical",
            }}
          />
          <div className="flex items-center gap-2">
            {onBlockApprove && (
              <button
                onClick={() => {
                  onBlockApprove(block.blockId, reviewNotes || undefined);
                  setReviewNotes("");
                  setShowReview(false);
                }}
                className="text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{
                  backgroundColor: "var(--c-surface)",
                  color: "var(--c-success)",
                  border: "1px solid var(--c-success)",
                }}
              >
                Approve
              </button>
            )}
            {onBlockReject && (
              <button
                onClick={() => {
                  onBlockReject(block.blockId, reviewNotes || undefined);
                  setReviewNotes("");
                  setShowReview(false);
                }}
                className="text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{
                  backgroundColor: "var(--c-surface)",
                  color: "var(--c-danger)",
                  border: "1px solid var(--c-danger)",
                }}
              >
                Reject
              </button>
            )}
          </div>
        </div>
      )}

      {/* Block actions (display-only for actions defined in RenderSpec) */}
      {block.actions && block.actions.length > 0 && !showReview && (
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{ borderTop: "1px solid var(--c-border)" }}
        >
          {block.actions.map((action) => (
            <button
              key={action.actionId}
              className="text-xs px-2 py-1 rounded"
              style={{
                backgroundColor: "var(--c-surface)",
                color:
                  action.type === "approve"
                    ? "var(--c-success)"
                    : action.type === "reject"
                      ? "var(--c-danger)"
                      : "var(--c-text-secondary)",
                border: "1px solid var(--c-border)",
                cursor: interactive ? "pointer" : "default",
                opacity: interactive ? 1 : 0.7,
              }}
              disabled={!interactive}
              title={interactive ? action.label : "Actions available when interactive"}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
